import React, { useEffect, useState } from "react";
import { BallView } from "@/components/BallView";
import { DesignField } from "@/components/DesignField";
import { BreathingRing } from "@/components/UIBits";
import { T } from "@/lib/copy";
import { api } from "@/lib/api";
import { audioCtl, playLine } from "@/lib/audio";
import { Zap, ChevronRight } from "lucide-react";

const OUTCOME_LABEL = { "6": "SIX", "4": "FOUR", "2": "TWO", "1": "SINGLE", "0": "DOT", W: "WICKET" };
const OUTCOME_STATE = { "6": "blaze", "4": "blaze", "2": "warm", "1": "warm", "0": "dim", W: "cold" };
const OUTCOME_COLOR = { blaze: "var(--sm-blaze)", warm: "var(--sm-floodlight)", dim: "#8E97A8", cold: "var(--sm-steel)" };

function ElimBanner({ t, round, final }) {
  return (
    <div className="text-center" data-testid="eliminator-banner">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-mono text-[11px] up tracking-[0.16em]"
        style={{ border: "1px solid rgba(255,176,102,0.6)", color: "var(--sm-blaze)", background: "rgba(232,132,44,0.08)" }}>
        <Zap size={13} /> {final ? "SUDDEN DEATH · GOLDEN BALL" : `${t.elimRound} ${round} · SUDDEN DEATH`}
      </div>
    </div>
  );
}

export default function Eliminator({ lang, overId, persona, onDone }) {
  const t = T(lang);
  const [stage, setStage] = useState("loading"); // loading | intro | ball | reveal | result
  const [intro, setIntro] = useState(null);
  const [round, setRound] = useState(1);
  const [ballNo, setBallNo] = useState(1);
  const [final, setFinal] = useState(false);
  const [ball, setBall] = useState(null);
  const [timerMs, setTimerMs] = useState(45000);
  const [ans, setAns] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.elimStart(overId, lang)
      .then((s) => {
        if (!alive) return;
        setIntro(s.intro); setRound(s.round); setBallNo(s.ball_no);
        setFinal(!!s.final); setTimerMs(s.timer_ms || 45000); setStage("intro");
      })
      .catch(() => onDone(null));
    return () => { alive = false; audioCtl.stop(); };
    // eslint-disable-next-line
  }, []);

  async function loadBall(r, b) {
    setStage("loading");
    try {
      const d = await api.elimBall(overId, r, b);
      setBall(d.ball); setRound(d.round); setBallNo(d.ball_no);
      setFinal(!!d.final); setTimerMs(d.timer_ms || 45000); setStage("ball");
    } catch (e) { onDone(null); }
  }

  async function onLock(optionIndex, ms) {
    try {
      const a = await api.elimAnswer(overId, round, ballNo, optionIndex < 0 ? -1 : optionIndex, ms, lang);
      setAns(a); setStage("reveal");
      if (a && a.call) playLine({ text: a.call, persona, lang, situation: "eliminator" });
    } catch (e) { onDone(null); }
  }

  function advance() {
    if (!ans) return;
    if (ans.status === "continue") { loadBall(ans.round, ans.next_ball_no); }
    else if (ans.status === "next_round") {
      setIntro(ans.intro); setRound(ans.round); setBallNo(ans.next_ball_no); setFinal(!!ans.final); setStage("intro");
    } else { setResult(ans.status); setStage("result"); }
  }

  if (stage === "loading") {
    return <div className="so-pad so-pad-top flex items-center justify-center min-h-full"><BreathingRing label="ELIMINATOR" /></div>;
  }

  if (stage === "intro") {
    return (
      <div className="so-pad so-pad-top flex flex-col min-h-full items-center justify-center text-center gap-6" data-testid="eliminator-intro">
        <ElimBanner t={t} round={round} final={final} />
        <div className="w-full max-w-xs"><DesignField field="STD" /></div>
        <div className="font-score italic up" style={{ fontSize: 30, color: "var(--sm-blaze)", textShadow: "0 0 26px rgba(255,176,102,0.4)" }}>{intro && intro.title}</div>
        <p className="font-reading text-[15px] leading-relaxed" style={{ color: "rgba(244,239,230,0.92)", maxWidth: 320 }}>{intro && intro.rule}</p>
        <button className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2" onClick={() => loadBall(round, ballNo)} data-testid="eliminator-bring-it-button">
          <Zap size={18} /> {t.elimBringIt}
        </button>
      </div>
    );
  }

  if (stage === "ball" && ball) {
    return (
      <div data-testid="eliminator-screen">
        <BallView ball={ball} timerMs={timerMs} onLock={onLock} lang={lang}
          banner={<ElimBanner t={t} round={round} final={final} />} />
      </div>
    );
  }

  if (stage === "reveal" && ans) {
    const st = OUTCOME_STATE[ans.outcome] || "warm";
    const decided = ans.status === "won" || ans.status === "lost";
    return (
      <div className="so-pad so-pad-top flex flex-col min-h-full items-center text-center gap-5" data-testid="eliminator-reveal">
        <ElimBanner t={t} round={ans.round || round} final={final} />
        <div className="w-full max-w-xs"><DesignField field="STD" released={ans.outcome !== "W"} /></div>
        <div className="font-score italic up anim-blaze" style={{ fontSize: 44, color: OUTCOME_COLOR[st], textShadow: st === "cold" ? "none" : `0 0 28px ${OUTCOME_COLOR[st]}66` }} data-testid="eliminator-outcome">
          {OUTCOME_LABEL[ans.outcome] || ans.outcome}
        </div>
        {ans.reveal && <p className="font-reading text-[15px] italic leading-relaxed" style={{ color: "var(--sm-pitch)", maxWidth: 320 }}>{ans.reveal}</p>}
        <button className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 mt-auto" onClick={advance} data-testid="eliminator-next-button">
          {decided ? t.elimSeeResult : t.elimNext} <ChevronRight size={18} />
        </button>
      </div>
    );
  }

  if (stage === "result") {
    const won = result === "won";
    return (
      <div className="so-pad so-pad-top flex flex-col min-h-full items-center justify-center text-center gap-6" data-testid="eliminator-result">
        <div className="w-full max-w-xs"><DesignField field="STD" /></div>
        <div className="font-score italic up anim-blaze" style={{ fontSize: 38, color: won ? "var(--sm-blaze)" : "var(--sm-steel)", textShadow: won ? "0 0 30px rgba(255,176,102,0.45)" : "none" }}>
          {won ? t.elimWon : t.elimLost}
        </div>
        <p className="font-reading text-[15px] leading-relaxed" style={{ color: "rgba(244,239,230,0.9)", maxWidth: 320 }}>{won ? t.elimWonSub : t.elimLostSub}</p>
        <button className="btn-primary w-full py-4 text-lg" onClick={() => onDone(result)} data-testid="eliminator-result-back-button">{t.elimBackToSummary}</button>
      </div>
    );
  }

  return <div className="so-pad so-pad-top flex items-center justify-center min-h-full"><BreathingRing label="ELIMINATOR" /></div>;
}
